import React, {memo, ReactNode} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {
  FullHTMLLink,
  Props as FullHTMLLinkProps,
} from 'app/components/general/FullHTMLLink';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {AttachTooltip} from 'app/general/overlays';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {gt} from 'app/utils/i18n';

import type {AbstractVersionType} from 'app/version/types';

type Props<V extends AbstractVersionType> = {
  canDelete: boolean;
  searchParams?: string;
  isSelected?: boolean;
  onDelete: () => Promise<any>;
  version: V;
  compactDraftLayout?: boolean;
  children?: ReactNode;
};

const BaseVersionItem = <V extends AbstractVersionType>({
  canDelete,
  version: {
    html_url,
    editor,
    created_at,
    approved_at,
    restorable,
    is_current,
    signoff_requested_at,
    comment_count,
  },
  searchParams,
  isSelected,
  onDelete,
  children,
  compactDraftLayout = false,
}: Props<V>) => {
  const toolTipText = gt(
    'Request a change to per user pricing for Unlimited version history.'
  );

  const currentVersion = is_current ? (
    <LazyTooltip
      placement="top"
      tooltip={
        <span>
          {gt('This version is seen by people viewing the document.')}
        </span>
      }
    >
      <span className="badge float-right badge-success">{gt('live')}</span>
    </LazyTooltip>
  ) : null;

  const approvedBadge = (
    <span className="badge float-right badge-primary">{gt('approved')}</span>
  );

  const draftBadge = (
    <span className="float-right badge badge-secondary">{gt('draft')}</span>
  );

  const signoffsBadge = signoff_requested_at ? (
    <LazyTooltip
      placement="top"
      tooltip={<span>{gt('Sign Offs are required for this version.')}</span>}
    >
      <span className="mx-1 float-right badge badge-light">
        {gt('sign off')}
      </span>
    </LazyTooltip>
  ) : null;

  const statusBadge =
    typeof approved_at === 'string'
      ? currentVersion || approvedBadge
      : draftBadge;

  const commentsBadge =
    comment_count && comment_count > 0 ? (
      <span className="badge-light text-secondary badge float-right mr-1">
        <i className="fa fa-comment" /> {comment_count}
      </span>
    ) : null;

  const createdAtElement = (
    <ElapsedTime
      className="align-items-center"
      tooltipPlacement="top"
      ContainerTag="small"
      timestamp={created_at}
      wrapTooltip={(text) => <small>{gt('Created %s', text)}</small>}
    />
  );

  const authorNameElement = (
    <span className="small font-weight-bold">
      {editor != null ? editor.name : gt('Deleted user')}
    </span>
  );

  const actionMenuElement =
    !is_current && restorable && onDelete != null && canDelete ? (
      <ActionsButton
        actions={[
          {
            text: gt('Move to Recycle Bin'),
            icon: 'far fa-trash',
            key: 'delete',
            callback: onDelete,
          },
        ]}
      />
    ) : null;

  const renderItemContent = () => (
    <li
      className={cs('list-group-item p-0 d-flex', {
        active: isSelected,
      })}
      style={
        compactDraftLayout
          ? {
              borderBottom: 'none',
              borderTopStyle: 'dotted',
            }
          : {}
      }
    >
      {compactDraftLayout ? (
        <div
          className={cs('px-3', {
            'bg-light': !isSelected,
          })}
        />
      ) : null}

      <StyledContainerFullHTMLLink
        className={cs('flex-grow-1', children != null ? 'pb-0' : 'pb-2', {
          'non-restorable': restorable !== true,
          selected: isSelected,
        })}
        html_url={restorable ? html_url + (searchParams || '') : ''}
        replace={false}
      >
        {compactDraftLayout ? (
          <React.Fragment>
            <div className="d-flex justify-content-between align-items-center">
              {createdAtElement}
              <div className="d-flex align-items-center">
                {signoffsBadge}
                {statusBadge}
                {commentsBadge}
                {actionMenuElement}
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="clearfix editor mb-1">
              {commentsBadge}
              {authorNameElement}
            </div>
            <div className="d-flex justify-content-between align-items-center">
              {createdAtElement}
              <div className="d-flex align-items-center">
                {statusBadge}
                {signoffsBadge}
                {actionMenuElement}
              </div>
            </div>
          </React.Fragment>
        )}
      </StyledContainerFullHTMLLink>
      {children}
    </li>
  );

  if (!restorable) {
    return (
      <AttachTooltip placement="top" tooltipContent={toolTipText}>
        {renderItemContent}
      </AttachTooltip>
    );
  }

  return renderItemContent();
};

export const VersionItem = memo<Props<AbstractVersionType>>(BaseVersionItem);

const StyledContainerFullHTMLLink = styled(
  FullHTMLLink
).attrs<FullHTMLLinkProps>({
  className: 'clearfix px-2 pt-2',
})`
  display: block;
  color: ${colors.textDefault};

  &.selected {
    color: #fff;

    button:not(.dropdown-item) {
      color: #fff;
    }
  }

  &.non-restorable {
    color: #ccc;
    cursor: not-allowed;

    &:hover {
      color: #ccc;
    }
  }

  .editor {
    line-height: 1;
  }
`;
